// src/components/Login/ForgotPassword.jsx
// ======================================================
// 🔐 FORGOT PASSWORD - NIVEL AIRBNB/ZILLOW
// ======================================================
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Mail, 
  ArrowLeft, 
  Send, 
  CheckCircle, 
  AlertCircle,
  Clock,
  Shield,
  RefreshCw
} from 'lucide-react';

// ✅ CORRECCIÓN: Import default (sin llaves)
import authService from '../../services/authService';
// También mantenemos emailService para envío real de emails
import { sendResetPasswordEmail } from '../../services/emailService';

/**
 * Componente de recuperação de senha ultra premium
 * Nível: Google/Airbnb/Spotify
 */

// ============================================
// ⏱️ CONSTANTES
// ============================================
const RESEND_COOLDOWN = 60; // segundos
const MAX_ATTEMPTS = 3; // máx tentativas
const BLOCK_TIME = 900000; // 15 minutos (ms)

const ForgotPassword = ({ onBackToLogin, onEmailSent }) => {
  // ============================================
  // 📊 ESTADOS
  // ============================================
  const [email, setEmail] = useState('');
  const [isValid, setIsValid] = useState(false);
  const [touched, setTouched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(null); // 'success' | 'error' | 'rate-limited'
  const [errorMessage, setErrorMessage] = useState('');
  const [cooldown, setCooldown] = useState(0);
  const [sentEmails, setSentEmails] = useState([]);
  
  // Rate limiting
  const [attempts, setAttempts] = useState(() => {
    const saved = localStorage.getItem('reset_attempts');
    return saved ? JSON.parse(saved) : { count: 0, timestamp: Date.now() };
  });
  
  const [isBlocked, setIsBlocked] = useState(false);

  // ============================================
  // 🔒 VERIFICAR RATE LIMITING
  // ============================================
  useEffect(() => {
    const checkRateLimit = () => {
      const now = Date.now();
      
      // Resetar tentativas após 15 minutos
      if (now - attempts.timestamp > BLOCK_TIME) {
        setAttempts({ count: 0, timestamp: now });
        setIsBlocked(false);
        localStorage.setItem('reset_attempts', JSON.stringify({ count: 0, timestamp: now }));
      } else if (attempts.count >= MAX_ATTEMPTS) {
        setIsBlocked(true);
        const minutesLeft = Math.ceil((BLOCK_TIME - (now - attempts.timestamp)) / 60000);
        setErrorMessage(`Muitas tentativas. Tente novamente em ${minutesLeft} minutos.`);
        setStatus('rate-limited');
      }
    };
    
    checkRateLimit();
  }, [attempts]);

  // ============================================
  // 🔍 VALIDAÇÃO DE EMAIL
  // ============================================
  const validateEmail = (value) => {
    if (!value) return 'E-mail é obrigatório';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
      return 'E-mail inválido';
    }
    if (value.length > 100) return 'E-mail muito longo';
    return null;
  };

  const handleEmailChange = (e) => {
    const value = e.target.value;
    setEmail(value);
    
    // Validação em tempo real
    const error = validateEmail(value);
    setIsValid(!error);
    
    // Limpar status se estiver editando
    if (status) {
      setStatus(null);
    }
  };

  const handleBlur = () => {
    setTouched(true);
  };

  // ============================================
  // ⏱️ TIMER DE COOLDOWN
  // ============================================
  const startCooldown = () => {
    setCooldown(RESEND_COOLDOWN);
    
    const timer = setInterval(() => {
      setCooldown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    
    return () => clearInterval(timer);
  };

  // ============================================
  // 📧 ENVIO DE EMAIL - CORREGIDO
  // ============================================
  const sendEmail = async (emailToSend) => {
    console.log('📧 Iniciando envio para:', emailToSend);
    
    try {
      // ✅ CORRECCIÓN: Usar authService.forgotPassword (método correcto)
      const result = await authService.forgotPassword(emailToSend);
      
      if (!result.success) {
        throw new Error(result.message || 'Erro ao enviar email');
      }
      
      // Si hay servicio de email real, usarlo
      if (sendResetPasswordEmail && result.token) {
        await sendResetPasswordEmail(emailToSend, result.token);
      }
      
      return result;
      
    } catch (error) {
      console.error('❌ Erro no envio:', error);
      throw error;
    }
  };

  // ============================================
  // 📊 REGISTRAR TENTATIVA
  // ============================================
  const registerAttempt = () => {
    const now = Date.now();
    const newAttempts = {
      count: attempts.count + 1,
      timestamp: attempts.timestamp
    };
    
    setAttempts(newAttempts);
    localStorage.setItem('reset_attempts', JSON.stringify(newAttempts));
  };

  // ============================================
  // 🎯 HANDLER DE SUBMISSÃO
  // ============================================
  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    
    // Verificar se está bloqueado
    if (isBlocked) {
      setStatus('rate-limited');
      return;
    }
    
    // Validar email
    const error = validateEmail(email);
    if (error) {
      setTouched(true);
      setStatus('error');
      setErrorMessage(error);
      return;
    }
    
    setLoading(true);
    setStatus(null);
    
    try {
      // Enviar email usando authService
      const result = await sendEmail(email);
      
      // Registrar tentativa bem sucedida
      registerAttempt();
      
      // Registrar email enviado
      setSentEmails(prev => [...prev, email]);
      
      // Iniciar cooldown
      startCooldown();
      
      // Mostrar sucesso
      setStatus('success');
      
      // Log para debug
      console.log('✅ Email processado com sucesso para:', email);
      
      // Callback opcional
      if (onEmailSent) {
        onEmailSent(email, result.token);
      }
      
    } catch (error) {
      console.error('❌ Erro ao processar solicitação:', error);
      
      // Registrar tentativa falha
      registerAttempt();
      
      setStatus('error');
      
      // Mensagem amigável
      if (error.message.includes('encontrado')) {
        setErrorMessage('Email não encontrado em nossa base.');
      } else if (error.message.includes('conexão') || error.message.includes('network')) {
        setErrorMessage('Erro de conexão. Verifique sua internet.');
      } else {
        setErrorMessage('Erro ao processar solicitação. Tente novamente.');
      }
    } finally {
      setLoading(false);
    }
  };

  // ============================================
  // 🔄 HANDLER DE REENVIO
  // ============================================
  const handleResend = async () => {
    if (cooldown > 0 || loading || isBlocked) return;
    await handleSubmit(null);
  };

  // ============================================
  // 🎨 RENDERIZAÇÃO
  // ============================================
  const emailError = touched && validateEmail(email);

  // Renderizar bloqueio
  if (isBlocked) {
    return (
      <div className="w-full max-w-md mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-2xl shadow-xl p-8 border border-slate-100 text-center"
        >
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            className="w-20 h-20 bg-red-100 rounded-full mx-auto mb-6 flex items-center justify-center"
          >
            <Clock className="w-10 h-10 text-red-600" />
          </motion.div>
          
          <h2 className="text-2xl font-bold text-slate-900 mb-3">
            Muitas tentativas
          </h2>
          
          <p className="text-slate-600 mb-6">
            {errorMessage}
          </p>
          
          <button
            onClick={onBackToLogin}
            className="w-full py-3 px-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl font-semibold hover:from-blue-700 hover:to-purple-700 transition-all"
          >
            Voltar para o login
          </button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-md mx-auto">
      {/* HEADER */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-center mb-8"
      >
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ type: 'spring', stiffness: 200, delay: 0.1 }}
          className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl mx-auto mb-4 flex items-center justify-center shadow-lg"
        >
          <Shield className="w-8 h-8 text-white" />
        </motion.div>
        
        <h1 className="text-2xl font-bold text-slate-900 mb-2">
          Recuperar senha
        </h1>
        <p className="text-slate-600">
          Enviaremos um link para você redefinir sua senha
        </p>
      </motion.div>

      {/* FORMULÁRIO */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="bg-white rounded-2xl shadow-xl p-6 border border-slate-100"
      >
        {status === 'success' ? (
          /* ESTADO DE SUCESSO */
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="text-center py-6"
          >
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: 'spring', stiffness: 200 }}
              className="w-16 h-16 bg-emerald-100 rounded-full mx-auto mb-4 flex items-center justify-center"
            >
              <CheckCircle className="w-8 h-8 text-emerald-600" />
            </motion.div>
            
            <h3 className="text-lg font-semibold text-slate-900 mb-2">
              E-mail enviado!
            </h3>
            
            <p className="text-sm text-slate-600 mb-4">
              Enviamos instruções para <br />
              <span className="font-medium text-slate-900">{email}</span>
            </p>
            
            <div className="bg-blue-50 rounded-xl p-4 text-left mb-6">
              <p className="text-xs text-blue-700 mb-2 flex items-center gap-1">
                <Clock className="w-3 h-3" />
                Importante:
              </p>
              <ul className="text-xs text-blue-600 space-y-1">
                <li>• O link expira em 1 hora</li>
                <li>• Verifique sua caixa de SPAM</li>
                <li>• Se não encontrar, clique em reenviar</li>
              </ul>
            </div>
            
            <div className="space-y-3">
              <button
                onClick={handleResend}
                disabled={cooldown > 0 || loading}
                className={`
                  w-full py-2.5 px-4 rounded-xl font-medium
                  transition-all flex items-center justify-center gap-2
                  ${cooldown > 0 || loading
                    ? 'bg-slate-100 text-slate-400 cursor-not-allowed'
                    : 'bg-blue-50 text-blue-600 hover:bg-blue-100'
                  }
                `}
              >
                {loading ? (
                  <RefreshCw className="w-4 h-4 animate-spin" />
                ) : (
                  <Send className="w-4 h-4" />
                )}
                {cooldown > 0 
                  ? `Reenviar em ${cooldown}s` 
                  : 'Reenviar e-mail'}
              </button>
              
              <button
                onClick={onBackToLogin}
                className="w-full py-2.5 px-4 rounded-xl font-medium text-slate-600 hover:bg-slate-50 transition-all flex items-center justify-center gap-2"
              >
                <ArrowLeft className="w-4 h-4" />
                Voltar para o login
              </button>
            </div>
          </motion.div>
        ) : (
          /* FORMULÁRIO PRINCIPAL */
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Campo de email */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-slate-700">
                <div className="flex items-center gap-2 mb-1">
                  <Mail className="w-4 h-4" />
                  E-mail
                </div>
              </label>
              
              <div className="relative">
                <input
                  type="email"
                  value={email}
                  onChange={handleEmailChange}
                  onBlur={handleBlur}
                  className={`
                    w-full px-4 py-3 pl-11 pr-11 border rounded-xl
                    transition-all duration-200
                    focus:outline-none focus:ring-2 focus:ring-blue-500/20
                    ${touched && emailError
                      ? 'border-red-300 bg-red-50'
                      : touched && isValid
                        ? 'border-green-300 bg-green-50'
                        : 'border-slate-300'
                    }
                  `}
                  placeholder="seu@email.com"
                  disabled={loading}
                />
                <Mail className="absolute left-3 top-3.5 w-5 h-5 text-slate-400" />
                
                {touched && isValid && (
                  <CheckCircle className="absolute right-3 top-3.5 w-5 h-5 text-green-500" />
                )}
              </div>
              
              <AnimatePresence>
                {touched && emailError && (
                  <motion.p
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className="text-xs text-red-500 flex items-center gap-1"
                  >
                    <AlertCircle className="w-3 h-3" />
                    {emailError}
                  </motion.p>
                )}
              </AnimatePresence>
            </div>

            {/* Informação de tentativas */}
            {attempts.count > 0 && (
              <p className="text-xs text-amber-600 flex items-center gap-1">
                <Clock className="w-3 h-3" />
                Tentativas: {attempts.count}/{MAX_ATTEMPTS}
              </p>
            )}

            {/* Mensagem de erro */}
            <AnimatePresence>
              {status === 'error' && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="p-3 bg-red-50 border border-red-200 rounded-xl flex items-start gap-2"
                >
                  <AlertCircle className="w-4 h-4 text-red-600 mt-0.5" />
                  <p className="text-xs text-red-700 flex-1">
                    {errorMessage}
                  </p>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Botão de envio */}
            <motion.button
              type="submit"
              disabled={!isValid || loading || isBlocked}
              whileHover={isValid && !loading && !isBlocked ? { scale: 1.02 } : {}}
              whileTap={isValid && !loading && !isBlocked ? { scale: 0.98 } : {}}
              className={`
                w-full py-3.5 px-4 rounded-xl font-semibold
                transition-all flex items-center justify-center gap-2
                ${!isValid || loading || isBlocked
                  ? 'bg-slate-200 text-slate-500 cursor-not-allowed'
                  : 'bg-gradient-to-r from-blue-600 to-purple-600 text-white hover:from-blue-700 hover:to-purple-700 shadow-lg hover:shadow-xl'
                }
              `}
            >
              {loading ? (
                <>
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ repeat: Infinity, duration: 1, ease: 'linear' }}
                    className="w-4 h-4 border-2 border-white border-t-transparent rounded-full"
                  />
                  Enviando...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4" />
                  Enviar link de recuperação
                </>
              )}
            </motion.button>

            {/* Link para voltar */}
            <div className="text-center">
              <button
                type="button"
                onClick={onBackToLogin}
                className="text-sm text-slate-600 hover:text-slate-900 transition-colors flex items-center justify-center gap-1 mx-auto"
              >
                <ArrowLeft className="w-3 h-3" />
                Voltar para o login
              </button>
            </div>
          </form>
        )}
      </motion.div>

      {/* DEBUG INFO (apenas desenvolvimento) */}
      {process.env.NODE_ENV === 'development' && sentEmails.length > 0 && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="mt-4 p-3 bg-slate-100 rounded-xl text-xs"
        >
          <p className="font-medium text-slate-700 mb-1">🔧 Debug - Emails processados:</p>
          {sentEmails.map((emailAddr, idx) => (
            <p key={idx} className="text-slate-600">
              {idx + 1}. {emailAddr} - {new Date().toLocaleTimeString()}
            </p>
          ))}
        </motion.div>
      )}
    </div>
  );
};

// ✅ Otimização: memo para evitar re-renders desnecessários
export default React.memo(ForgotPassword);